/**
 * ShelfSidebar — Phase 6 (Patent Candidate I).
 *
 * Plan: docs/UX_V3_AI_NATIVE_RECONCEPTUALIZATION_PLAN.md
 *       §"Phase 6 — Shelf sidebar" + §"The creative sidebar
 *        (Orchestrator mode only)".
 *
 * Surface design:
 *   - 6 horizontal slim "shelves" stacked vertically: Plan, Design,
 *     Execute, Verify, Govern, and a 6th reserved Broadcast slot.
 *   - Hover-with-Intent: a 200 ms dwell timer triggers the shelf
 *     to open as a hover-intent (no pin). Pure hover-over does not
 *     trigger the open — this is the hover-of-death immunity.
 *   - Click: persistent open until another shelf is clicked. Pinned
 *     state transfers from the Zustand store and survives reloads.
 *   - Felix-driven: ``sidebar.open_category`` tool dispatches a
 *     window event that the store subscribes to. The pin lands the
 *     same as a user click.
 *
 * Patent I mechanism (do not refactor without the patent log):
 *   - Two-field state machine (openShelfId vs hoverIntentShelfId)
 *     is the hover-of-death-immune core.
 *   - The window-event bridge is the Felix-driven nav-surface state
 *     trigger; without it the patent claim collapses to "ordinary
 *     accordion sidebar".
 *
 * Motion contract:
 *   - Wrapped in <MotionConfig reducedMotion="user"> so users with
 *     prefers-reduced-motion get instant transitions.
 *   - Paused via useDocumentVisible when the tab is backgrounded
 *     (carry-forward from Phase 1).
 */

import { useEffect, useRef } from "react";
import { LayoutGroup, motion, MotionConfig } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import FTLIcon from "@/components/FTLIcon";
import { cn } from "@/lib/utils";
import { TYPE } from "@/lib/typography";
import { useDocumentVisible } from "@/hooks/useDocumentVisible";
import {
  SHELF_IDS,
  type ShelfId,
  subscribeToFelixCategoryOpen,
  useSidebarStore,
} from "@/stores/sidebarStore";
import { recordTelemetry } from "@/lib/telemetry";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import { PlanShelf } from "./shelves/PlanShelf";
import { DesignShelf } from "./shelves/DesignShelf";
import { ExecuteShelf } from "./shelves/ExecuteShelf";
import { VerifyShelf } from "./shelves/VerifyShelf";
import { GovernShelf } from "./shelves/GovernShelf";
import { BroadcastShelf } from "./shelves/BroadcastShelf";

// Hover-with-intent dwell. Mirrors the server default in
// app/services/sidebar_friction_config.py:DEFAULT_DWELL_MS. The Phase 7
// auto-PR pipeline tunes the server side; for now the client uses the
// stable default. A future enhancement will pull the per-user value
// from /api/v1/me/ui-friction at session start.
export const HOVER_INTENT_DWELL_MS = 200;

const SHELF_LABELS: Record<ShelfId, string> = {
  plan: "Plan",
  design: "Design",
  execute: "Execute",
  verify: "Verify",
  govern: "Govern",
  broadcast: "Broadcast",
};

interface Props {
  /** Project id flows down to the per-shelf entity cards. */
  projectId?: string | null;
  /** Phase 6 §6: override the default dwell (per-user friction
   *  override; server-resolved). */
  dwellMs?: number;
}

export function ShelfSidebar({
  projectId,
  dwellMs = HOVER_INTENT_DWELL_MS,
}: Props) {
  const openShelfId = useSidebarStore((s) => s.openShelfId);
  const hoverIntentShelfId = useSidebarStore((s) => s.hoverIntentShelfId);
  const setOpenShelf = useSidebarStore((s) => s.setOpenShelf);
  const setHoverIntentShelf = useSidebarStore(
    (s) => s.setHoverIntentShelf,
  );
  const clearHover = useSidebarStore((s) => s.clearHover);
  const documentVisible = useDocumentVisible();

  // Template-layer vocabulary SKIN — resolved ONCE for the whole sidebar and
  // threaded into every shelf, so a marketing project's cards read "Campaign
  // Goals"/"Channels"/… while a non-template project renders byte-identical to
  // today. One react-query fetch (keyed on projectId) feeds all 5 shelves.
  const { label, hiddenSurfaces } = useTemplateLabels(projectId ?? null);
  // Row 4aa3e0ec: a shelf whose surface the project type does not use (the
  // Broadcast shelf on a finance project) is left out entirely.
  const shelfIds = SHELF_IDS.filter((id) => !(hiddenSurfaces ?? []).includes(id));

  // Window-event bridge for Felix-driven opens.
  useEffect(() => subscribeToFelixCategoryOpen(), []);

  // Hover-with-intent timers, keyed by shelf id so a quick traverse
  // doesn't fire stale opens.
  const dwellTimers = useRef<Record<string, number>>({});
  // B3(a) — hover-of-death instrumentation. When a shelf opens via
  // hover-intent we stamp the open time; on hover-leave we emit the
  // elapsed `close_within_ms`. The Phase 7 friction analyzer's
  // _hover_then_close_rate reads this to compute the hover-of-death rate
  // (FR1, conf 0.92). Without it the squared w_hover term was always 0 and
  // FR1 was unreachable.
  const hoverOpenedAt = useRef<Record<string, number>>({});
  // A shelf that got clicked is NOT a hover-of-death close — track so the
  // close emit only counts genuine hover-then-abandon.
  const hoverClicked = useRef<Record<string, boolean>>({});

  const clearDwellTimer = (id: ShelfId) => {
    const t = dwellTimers.current[id];
    if (t) {
      window.clearTimeout(t);
      delete dwellTimers.current[id];
    }
  };
  useEffect(() => {
    return () => {
      // Drain all pending timers on unmount.
      Object.values(dwellTimers.current).forEach((t) =>
        window.clearTimeout(t),
      );
      dwellTimers.current = {};
    };
  }, []);

  const handleHoverEnter = (id: ShelfId) => {
    if (!documentVisible) return;
    clearDwellTimer(id);
    dwellTimers.current[id] = window.setTimeout(() => {
      setHoverIntentShelf(id);
      hoverOpenedAt.current[id] = Date.now();
      hoverClicked.current[id] = false;
      void recordTelemetry("iterator.sidebar_shelf_hovered", {
        category_id: id,
        dwell_ms: dwellMs,
      });
    }, dwellMs);
  };

  const handleHoverLeave = (id: ShelfId) => {
    clearDwellTimer(id);
    // Only clear if THIS shelf is the hover-intent one — leaving a
    // shelf you weren't pinned to shouldn't yank another shelf's
    // pin.
    if (hoverIntentShelfId === id) {
      // B3(a) — emit the close-within duration for the hover-of-death
      // detector. A hover-intent open that is left WITHOUT a click is a
      // hover-of-death candidate; close_within_ms lets the analyzer count
      // closes that happened in <500ms (the dwell threshold is too tight).
      const openedAt = hoverOpenedAt.current[id];
      if (openedAt && !hoverClicked.current[id]) {
        void recordTelemetry("iterator.sidebar_shelf_hovered", {
          category_id: id,
          dwell_ms: dwellMs,
          close_within_ms: Date.now() - openedAt,
        });
      }
      delete hoverOpenedAt.current[id];
      clearHover();
    }
  };

  const handleClick = (id: ShelfId) => {
    clearDwellTimer(id);
    hoverClicked.current[id] = true;
    if (openShelfId === id) {
      setOpenShelf(null);
      return;
    }
    setOpenShelf(id);
    void recordTelemetry("iterator.sidebar_shelf_clicked", {
      category_id: id,
      source: "click",
    });
  };

  // Resolution: pin wins over hover-intent (single-shelf-open
  // invariant — the Zustand setOpenShelf already nukes hover-intent
  // on pin, so this is belt-and-braces).
  const effectiveOpenId: ShelfId | null = openShelfId ?? hoverIntentShelfId;

  return (
    <MotionConfig reducedMotion="user">
      <aside
        data-testid="shelf-sidebar"
        aria-label="Project workspace shelves"
        className="fixed left-0 top-0 z-30 flex h-screen w-[304px] flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]"
      >
        {/* Brand header, the same height contract as the topbar so the borders
            meet (FelixRail and Sidebar carry the same one). Without it the
            top-left corner above the shelves was blank on every project page
            (row 8f61634b, the Keystone landing screenshot). */}
        <div
          data-testid="shelf-sidebar-brand"
          className="flex h-[var(--topbar-height)] min-w-0 shrink-0 items-center border-b border-[var(--color-border)] px-5"
        >
          <Link to="/" className="flex min-w-0 items-center gap-2.5 overflow-hidden" aria-label="FTL home">
            <FTLIcon size={40} interactive={false} />
            <span className="truncate text-lg font-bold tracking-tight text-[var(--color-text-primary)]">
              FTL
            </span>
          </Link>
        </div>
        <LayoutGroup id="shelf-sidebar">
          <nav role="navigation" className="flex flex-1 flex-col overflow-y-auto py-2">
            {shelfIds.map((id) => {
              const isOpen = effectiveOpenId === id;
              const isPinned = openShelfId === id;
              return (
                <motion.div
                  key={id}
                  layout
                  data-testid={`shelf-${id}`}
                  data-pinned={isPinned}
                  data-open={isOpen}
                  className="flex flex-col"
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-current={isPinned ? "true" : undefined}
                    aria-controls={`shelf-panel-${id}`}
                    onMouseEnter={() => handleHoverEnter(id)}
                    onMouseLeave={() => handleHoverLeave(id)}
                    onFocus={() => handleHoverEnter(id)}
                    onBlur={() => handleHoverLeave(id)}
                    onClick={() => handleClick(id)}
                    data-testid={`shelf-toggle-${id}`}
                    className={cn(
                      TYPE.bodyMed,
                      "flex items-center justify-between gap-2 px-4 py-3 text-left transition-colors",
                      isPinned
                        ? "bg-brand-600 font-semibold text-white"
                        : isOpen
                          ? "bg-[var(--color-surface-secondary)] text-[var(--color-text-primary)]"
                          : "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-primary)]",
                    )}
                  >
                    <span>{SHELF_LABELS[id]}</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.15 }}
                      aria-hidden="true"
                    >
                      <ChevronDown size={14} />
                    </motion.span>
                  </button>
                  {isOpen && (
                    <motion.div
                      id={`shelf-panel-${id}`}
                      role="group"
                      aria-label={`${SHELF_LABELS[id]} entities`}
                      layout
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden border-y border-[var(--color-border)]"
                    >
                      {id === "plan" && <PlanShelf projectId={projectId} label={label} />}
                      {id === "design" && <DesignShelf projectId={projectId} label={label} />}
                      {id === "execute" && <ExecuteShelf projectId={projectId} label={label} />}
                      {id === "verify" && <VerifyShelf projectId={projectId} label={label} />}
                      {id === "govern" && <GovernShelf projectId={projectId} />}
                      {id === "broadcast" && <BroadcastShelf projectId={projectId} />}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </nav>
        </LayoutGroup>
      </aside>
    </MotionConfig>
  );
}
